import PropTypes from 'prop-types';
import moment from 'moment';
import React, { useState } from 'react';
import { message } from 'antd';
import { reduxForm, SubmissionError } from 'redux-form';
import { validateForm } from '@zipdrug/ui';
import Cookies from 'js-cookie';
import { withSession } from '@zipdrug/react-api-sdk';
import withSsoLogin from 'hoc/withSsoLogin';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { useNavigate } from 'react-router-dom';

import PinLogin from './PinLogin';
import { setPasswordExpiration, setPharmacyId } from '../redux/queryReducer';
import { IS_NEW_BROWSER_SESSION } from '../routes/AuthenticatedComponent';
import { LAST_USER_ACTIVITY_COOKIE } from '../session-trackers/IdleTimerContainer';

const PIN_ERRORS = {
  invalidPin: 'Invalid PIN.',
  serverError: 'Something went wrong. Please contact support.',
};

const validate = values => {
  validateForm(values, {}, PIN_ERRORS);
};

const ReduxPinForm = reduxForm({
  form: 'pinLogin',
  validate,
})(PinLogin);

const PinLoginPage = ({ setUser, setPasswordExpiration, pinLogin, sendNewPin }) => {
  const navigate = useNavigate();
  const [resending, setResending] = useState(false);
  const [pinError, setPinError] = useState('');

  const handleLoginResponse = res => {
    if (res.expirationToken) {
      const expirationToken = res.expirationToken;
      navigate(`/reset-password?token=${expirationToken}&redirect=care`);
      return;
    }

    if (res.errors) {
      throw new SubmissionError(res.errors);
    }

    if (res.daysUntilPasswordExpires) {
      setPasswordExpiration({ passwordExpiration: res.daysUntilPasswordExpires });
    }

    Cookies.set(IS_NEW_BROWSER_SESSION, true);
    Cookies.set(LAST_USER_ACTIVITY_COOKIE, Date.now());

    setUser(res.user);

    navigate('/members', { state: { daysUntilPasswordExpires: 1 } });
  };

  const handleResendPin = () => {
    setResending(true);
    return sendNewPin({ email: history?.state?.usr?.userId })
      .catch(e => {
        const _error = e?.graphQLErrors[0]?.message;
        setResending(false);
        setPinError(_error);
      })
      .then(props => {
        if (props?.data) {
          message.success('Check your email for a new PIN');
        }
        setResending(false);
      });
  };

  const handleSubmit = ({ pin }) =>
    pinLogin({ email: history?.state?.usr?.userId, pin: +pin })
      .catch(e => {
        let _error = `${e?.graphQLErrors[0]?.message}`;
        if (_error.includes('ACCOUNT LOCKED')) {
          const newError = _error.replace('ACCOUNT LOCKED. PLEASE TRY AGAIN AFTER ', '');
          const lockDateTime = moment(new Date(newError)).format('MM/DD/YYYY h:mma');
          _error = `ACCOUNT LOCKED. PLEASE TRY AGAIN AFTER ${lockDateTime}`;
        }
        throw new SubmissionError({ _error });
      })
      .then(res => {
        if (res) {
          handleLoginResponse(res);
        }
      });

  return (
    <ReduxPinForm
      onSubmit={handleSubmit}
      onResendPin={handleResendPin}
      resending={resending}
      pinError={pinError}
    />
  );
};

PinLoginPage.propTypes = {
  history: PropTypes.object.isRequired,
  setUser: PropTypes.func,
  setPasswordExpiration: PropTypes.func,
  pinLogin: PropTypes.func,
  sendNewPin: PropTypes.func,
};

const mapDispatchToProps = dispatch => ({
  setUser(user) {
    const roles = user.roles.filter(r => r.pharmacy_id);
    if (roles.length) {
      dispatch(setPharmacyId({ pharmacy_id: roles[0].pharmacy_id }));
    }
  },
  setPasswordExpiration: ({ passwordExpiration }) =>
    dispatch(setPasswordExpiration({ passwordExpiration })),
});

export default compose(withSsoLogin, withSession, connect(null, mapDispatchToProps))(PinLoginPage);